// Alertas sonoros por arquivos .mp3 servidos como assets estáticos
// (`/assets/sounds/...`). Dois sons distintos: mensagem nova (`nova_mensagem.mp3`)
// e conversa nova na fila (`nova_conversa.mp3`).
//
// Política de autoplay: browsers só deixam tocar após um gesto do usuário. Por
// isso `unlockAudio()` (registrado uma vez em pointerdown/keydown) "prime" cada
// elemento de áudio (play mudo → pause) no primeiro gesto; depois disso os play*
// reproduzem mesmo com a aba sem foco.
//
// Preferência (RF-04): `isSoundEnabled()` lê `localStorage['chatia:sound']`
// (default ligado). Com o som desligado os play* viram no-op.

const MESSAGE_SRC = '/assets/sounds/nova_mensagem.mp3';
const QUEUE_SRC = '/assets/sounds/nova_conversa.mp3';

const SOUND_PREF_KEY = 'chatia:sound';

// --- Preferência liga/desliga (RF-04) -------------------------------------

/** Sons ligados? Default ligado quando a chave está ausente/inválida. */
export function isSoundEnabled(): boolean {
  if (typeof localStorage === 'undefined') return true;
  try {
    // Só 'off' desliga; qualquer outro valor (inclusive ausente) = ligado.
    return localStorage.getItem(SOUND_PREF_KEY) !== 'off';
  } catch {
    return true;
  }
}

/** Persiste a preferência de som. */
export function setSoundEnabled(enabled: boolean): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SOUND_PREF_KEY, enabled ? 'on' : 'off');
  } catch {
    // Storage cheio / desabilitado — ignora silenciosamente.
  }
}

// --- Elementos de áudio (lazy singleton por som) --------------------------

const elements: Partial<Record<string, HTMLAudioElement>> = {};

/** Cria/reusa o <audio> de um src. Null em SSR/sem suporte. */
function getAudio(src: string): HTMLAudioElement | null {
  if (typeof Audio === 'undefined') return null;
  const cached = elements[src];
  if (cached) return cached;
  const el = new Audio(src);
  el.preload = 'auto';
  elements[src] = el;
  return el;
}

// --- Unlock de autoplay ----------------------------------------------------

let unlocked = false;

/**
 * Registra um handler único (pointerdown + keydown) que "prime" cada áudio no
 * primeiro gesto do usuário: play mudo → pause → reset. Isso libera reproduções
 * programáticas futuras mesmo sem gesto direto. Idempotente.
 */
export function unlockAudio(): void {
  if (typeof window === 'undefined' || unlocked) return;
  unlocked = true;
  const handler = () => {
    for (const src of [MESSAGE_SRC, QUEUE_SRC]) {
      const el = getAudio(src);
      if (!el) continue;
      el.muted = true;
      const p = el.play();
      // play() pode retornar undefined em ambientes antigos/mocks.
      void Promise.resolve(p)
        .then(() => {
          el.pause();
          el.currentTime = 0;
          el.muted = false;
        })
        .catch(() => {
          // Bloqueio no prime é tolerável — a 1ª reprodução real tentará de novo.
          el.muted = false;
        });
    }
  };
  window.addEventListener('pointerdown', handler, { once: true });
  window.addEventListener('keydown', handler, { once: true });
}

// --- Throttle: cada som toca no máx. 1× por segundo (rajada não vira metralhadora).
const THROTTLE_MS = 1000;
const lastPlayedAt: Record<string, number> = {};

function throttled(key: string): boolean {
  const now = Date.now();
  if (now - (lastPlayedAt[key] ?? 0) < THROTTLE_MS) return true;
  lastPlayedAt[key] = now;
  return false;
}

/** Reproduz um som do início. Respeita preferência e throttle. */
function play(src: string, key: string): void {
  if (!isSoundEnabled()) return;
  if (throttled(key)) return;
  const el = getAudio(src);
  if (!el) return;
  el.currentTime = 0;
  const p = el.play();
  void Promise.resolve(p).catch((err: unknown) => {
    // Autoplay bloqueado ou erro de reprodução: registra no console e segue —
    // sem travar nem estourar erro na UI (RF-01 / RF-03).
    console.warn('[sound] reprodução bloqueada', err);
  });
}

/** Mensagem nova de cliente numa conversa minha. */
export function playMessageAlert(): void {
  play(MESSAGE_SRC, 'message');
}

/** Conversa nova entrou na fila de atendimento. */
export function playQueueAlert(): void {
  play(QUEUE_SRC, 'queue');
}

/** Só pra testes: reseta throttle, unlock e elementos de áudio. */
export function __resetSoundStateForTests(): void {
  lastPlayedAt.message = 0;
  lastPlayedAt.queue = 0;
  unlocked = false;
  delete elements[MESSAGE_SRC];
  delete elements[QUEUE_SRC];
}
